import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Loader2, Download, Printer, Save, User, Baby, Utensils, Moon, Bath, MessageCircle, Heart, Smile, Activity, Star, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import API from '@/config/api';
// API imported from config;

const DCFIntakeUnder2Form = ({ open, onClose, childId, childName, token }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  const [formData, setFormData] = useState({
    child_last_name: '',
    child_first_name: '',
    child_middle_initial: '',
    child_nickname: '',
    child_birthdate: '',
    parent_names: '',
    parent_phone: '',
    parent_address: '',
    first_day_attendance: '',
    health_conditions: null,
    health_conditions_describe: '',
    meals: {
      current_schedule: '',
      schedule_length: '',
      food_types: [],
      milk_type_specify: '',
      new_food_timetable: '',
      eating_position: '',
      eating_position_other: '',
      feeds_self: null,
      feeds_self_uses: [],
      special_feeding_problems: null,
      special_feeding_problems_specify: '',
      food_allergies: null,
      food_allergies_specify: '',
      favorite_foods: '',
      refused_foods: ''
    },
    sleep: {
      current_schedule: '',
      schedule_length: '',
      falls_asleep_easily: null,
      mood_upon_awakening: '',
      takes_toys_to_bed: null,
      toys_list: '',
      sleep_position_under_1: '',
      sleep_position_over_1: ''
    },
    diapering: {
      diaper_type: [],
      diapers_provided_by_parent: null,
      plastic_pants: '',
      plastic_pants_specify: '',
      sensitive_skin: null,
      frequent_diaper_rash: null,
      lotions_used: null,
      lotions_specify: '',
      toilet_training: null,
      toilet_training_routine: '',
      toilet_seat_type: [],
      regular_bowel: null,
      bowel_frequency: '',
      bowel_times: '',
      toileting_problems: null,
      toileting_problems_describe: ''
    },
    verbal_communication: {
      family_languages: [],
      language_other_specify: '',
      age_began_talking: '',
      speaks_in: [],
      special_needs_words: ''
    },
    comforting: {
      has_fussy_time: null,
      fussy_time_specify: '',
      fussy_time_handling: '',
      child_likes: [],
      child_likes_other: '',
      comfort_methods: ''
    },
    self_expression: {
      causes_anger: '',
      what_frightens: '',
      expresses_happiness: '',
      additional_comments: ''
    },
    physical_social: {
      abilities: [],
      used_to_playmates: null,
      comments: ''
    },
    miscellaneous: {
      favorite_indoor: '',
      favorite_outdoor: '',
      additional_info: ''
    },
    parent_signature: '',
    signature_date: ''
  });

  useEffect(() => {
    if (open && childId) {
      loadData();
    }
  }, [open, childId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/children/${childId}/dcf-intake-under2-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data,
          meals: { ...prev.meals, ...response.data.meals },
          sleep: { ...prev.sleep, ...response.data.sleep },
          diapering: { ...prev.diapering, ...response.data.diapering },
          verbal_communication: { ...prev.verbal_communication, ...response.data.verbal_communication },
          comforting: { ...prev.comforting, ...response.data.comforting },
          self_expression: { ...prev.self_expression, ...response.data.self_expression },
          physical_social: { ...prev.physical_social, ...response.data.physical_social },
          miscellaneous: { ...prev.miscellaneous, ...response.data.miscellaneous }
        }));
      }
    } catch (error) {
      console.error('Error loading intake data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/children/${childId}/dcf-intake-under2-data`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Intake form saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save intake form');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-0061`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DCF-F-CFS0061_Intake_Under2_${childName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-0061`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } catch (error) {
      toast.error('Failed to open for printing');
    }
  };

  const updateSection = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const toggleArrayItem = (section, field, item) => {
    setFormData(prev => {
      const current = prev[section][field] || [];
      const updated = current.includes(item)
        ? current.filter(i => i !== item)
        : [...current, item];
      return {
        ...prev,
        [section]: { ...prev[section], [field]: updated }
      };
    });
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2">Loading intake form...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dcf-intake-under2-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Baby className="w-5 h-5 text-pink-500" />
            DCF Intake for Child Under 2 Years (DCF-F-CFS0061)
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Official Wisconsin DCF intake form - {childName}
          </p>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-intake-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Form
          </Button>
          <Button variant="outline" onClick={handleDownload} className="gap-2" data-testid="download-intake-btn">
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="info" data-testid="tab-info">
              <User className="w-4 h-4 mr-1" />
              Info
            </TabsTrigger>
            <TabsTrigger value="meals" data-testid="tab-meals">
              <Utensils className="w-4 h-4 mr-1" />
              Meals
            </TabsTrigger>
            <TabsTrigger value="sleep" data-testid="tab-sleep">
              <Moon className="w-4 h-4 mr-1" />
              Sleep
            </TabsTrigger>
            <TabsTrigger value="care" data-testid="tab-care">
              <Bath className="w-4 h-4 mr-1" />
              Care
            </TabsTrigger>
            <TabsTrigger value="development" data-testid="tab-development">
              <Activity className="w-4 h-4 mr-1" />
              Development
            </TabsTrigger>
          </TabsList>

          {/* INFO TAB */}
          <TabsContent value="info" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Baby className="w-4 h-4 text-pink-500" />
                  Child Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input value={formData.child_last_name} onChange={(e) => setFormData({ ...formData, child_last_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input value={formData.child_first_name} onChange={(e) => setFormData({ ...formData, child_first_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>MI</Label>
                    <Input value={formData.child_middle_initial} onChange={(e) => setFormData({ ...formData, child_middle_initial: e.target.value })} maxLength={1} className="w-16" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nickname</Label>
                    <Input value={formData.child_nickname} onChange={(e) => setFormData({ ...formData, child_nickname: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Birthdate *</Label>
                    <Input type="date" value={formData.child_birthdate} onChange={(e) => setFormData({ ...formData, child_birthdate: e.target.value })} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Parent/Guardian Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Parent(s) Name (Last, First, MI)</Label>
                  <Input value={formData.parent_names} onChange={(e) => setFormData({ ...formData, parent_names: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={formData.parent_phone} onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>First Day of Attendance</Label>
                    <Input type="date" value={formData.first_day_attendance} onChange={(e) => setFormData({ ...formData, first_day_attendance: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input value={formData.parent_address} onChange={(e) => setFormData({ ...formData, parent_address: e.target.value })} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200">
              <CardHeader className="pb-2 bg-amber-50 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <Heart className="w-4 h-4" />
                  Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <p className="text-xs text-muted-foreground">
                  Note: Health conditions that may affect care must be recorded in the child's health history record.
                </p>
                <div className="space-y-2">
                  <Label>Child has frequent colds, ear infections, colic, etc.?</Label>
                  <RadioGroup
                    value={formData.health_conditions === true ? 'yes' : formData.health_conditions === false ? 'no' : ''}
                    onValueChange={(v) => setFormData({ ...formData, health_conditions: v === 'yes' })}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="health-yes" />
                      <Label htmlFor="health-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="health-no" />
                      <Label htmlFor="health-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                {formData.health_conditions && (
                  <div className="space-y-2">
                    <Label>Describe:</Label>
                    <Textarea value={formData.health_conditions_describe} onChange={(e) => setFormData({ ...formData, health_conditions_describe: e.target.value })} rows={3} />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MEALS TAB */}
          <TabsContent value="meals" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-orange-500" />
                  Feeding Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Current feeding schedule</Label>
                  <Textarea value={formData.meals.current_schedule} onChange={(e) => updateSection('meals', 'current_schedule', e.target.value)} rows={2} placeholder="e.g., 7am, 11am, 3pm, 7pm" />
                </div>
                <div className="space-y-2">
                  <Label>Length of time on current schedule</Label>
                  <Input value={formData.meals.schedule_length} onChange={(e) => updateSection('meals', 'schedule_length', e.target.value)} placeholder="e.g., 3 months" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Food Type</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-4">
                  {['breast_milk', 'formula', 'strained', 'junior', 'table'].map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.meals.food_types?.includes(type)}
                        onCheckedChange={() => toggleArrayItem('meals', 'food_types', type)}
                      />
                      <span className="capitalize">{type.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label>Milk type (specify if applicable)</Label>
                  <Input value={formData.meals.milk_type_specify} onChange={(e) => updateSection('meals', 'milk_type_specify', e.target.value)} placeholder="e.g., Whole milk, 2%, Soy" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Eating Habits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>When eating, child is:</Label>
                  <RadioGroup
                    value={formData.meals.eating_position}
                    onValueChange={(v) => updateSection('meals', 'eating_position', v)}
                    className="flex flex-wrap gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="held_lap" id="eat-lap" />
                      <Label htmlFor="eat-lap">Held in lap</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="highchair" id="eat-high" />
                      <Label htmlFor="eat-high">In highchair</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="other" id="eat-other" />
                      <Label htmlFor="eat-other">Other</Label>
                    </div>
                  </RadioGroup>
                  {formData.meals.eating_position === 'other' && (
                    <Input value={formData.meals.eating_position_other} onChange={(e) => updateSection('meals', 'eating_position_other', e.target.value)} placeholder="Specify" className="mt-2" />
                  )}
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Feeds self?</Label>
                  <RadioGroup
                    value={formData.meals.feeds_self === true ? 'yes' : formData.meals.feeds_self === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('meals', 'feeds_self', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="feeds-yes" />
                      <Label htmlFor="feeds-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="feeds-no" />
                      <Label htmlFor="feeds-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.meals.feeds_self && (
                    <div className="flex gap-4 mt-2">
                      {['spoon', 'fork', 'hands'].map(item => (
                        <label key={item} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.meals.feeds_self_uses?.includes(item)}
                            onCheckedChange={() => toggleArrayItem('meals', 'feeds_self_uses', item)}
                          />
                          <span className="capitalize">{item}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Special feeding problems?</Label>
                  <RadioGroup
                    value={formData.meals.special_feeding_problems === true ? 'yes' : formData.meals.special_feeding_problems === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('meals', 'special_feeding_problems', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="feeding-prob-yes" />
                      <Label htmlFor="feeding-prob-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="feeding-prob-no" />
                      <Label htmlFor="feeding-prob-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.meals.special_feeding_problems && (
                    <Textarea value={formData.meals.special_feeding_problems_specify} onChange={(e) => updateSection('meals', 'special_feeding_problems_specify', e.target.value)} placeholder="Describe problems" rows={2} className="mt-2" />
                  )}
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Food allergies?</Label>
                  <RadioGroup
                    value={formData.meals.food_allergies === true ? 'yes' : formData.meals.food_allergies === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('meals', 'food_allergies', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="food-allergy-yes" />
                      <Label htmlFor="food-allergy-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="food-allergy-no" />
                      <Label htmlFor="food-allergy-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.meals.food_allergies && (
                    <Textarea value={formData.meals.food_allergies_specify} onChange={(e) => updateSection('meals', 'food_allergies_specify', e.target.value)} placeholder="List allergies" rows={2} className="mt-2" />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Food Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Favorite foods</Label>
                  <Textarea value={formData.meals.favorite_foods} onChange={(e) => updateSection('meals', 'favorite_foods', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Refused foods</Label>
                  <Textarea value={formData.meals.refused_foods} onChange={(e) => updateSection('meals', 'refused_foods', e.target.value)} rows={2} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SLEEP TAB */}
          <TabsContent value="sleep" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Moon className="w-4 h-4 text-indigo-500" />
                  Sleep Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Current sleep schedule</Label>
                  <Textarea value={formData.sleep.current_schedule} onChange={(e) => updateSection('sleep', 'current_schedule', e.target.value)} rows={2} placeholder="e.g., Nap 10am-12pm, Bedtime 7pm" />
                </div>
                <div className="space-y-2">
                  <Label>Length of time on current schedule</Label>
                  <Input value={formData.sleep.schedule_length} onChange={(e) => updateSection('sleep', 'schedule_length', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Falls asleep easily?</Label>
                  <RadioGroup
                    value={formData.sleep.falls_asleep_easily === true ? 'yes' : formData.sleep.falls_asleep_easily === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('sleep', 'falls_asleep_easily', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="sleep-easy-yes" />
                      <Label htmlFor="sleep-easy-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="sleep-easy-no" />
                      <Label htmlFor="sleep-easy-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label>Mood upon awakening</Label>
                  <Input value={formData.sleep.mood_upon_awakening} onChange={(e) => updateSection('sleep', 'mood_upon_awakening', e.target.value)} placeholder="e.g., Happy, Cranky, Hungry" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Comfort Items (Child over 1 year)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Takes favorite toy(s) to bed?</Label>
                  <RadioGroup
                    value={formData.sleep.takes_toys_to_bed === true ? 'yes' : formData.sleep.takes_toys_to_bed === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('sleep', 'takes_toys_to_bed', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="toys-yes" />
                      <Label htmlFor="toys-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="toys-no" />
                      <Label htmlFor="toys-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.sleep.takes_toys_to_bed && (
                    <Input value={formData.sleep.toys_list} onChange={(e) => updateSection('sleep', 'toys_list', e.target.value)} placeholder="List toy(s)" className="mt-2" />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200">
              <CardHeader className="pb-2 bg-red-50 rounded-t-lg">
                <CardTitle className="text-base text-red-800">Sleep Position (Important Safety Information)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="p-3 bg-red-100 border border-red-300 rounded text-sm text-red-800">
                  <strong>Note:</strong> Children under 1 year must be placed to sleep on their back unless a written statement from the child's physician is attached.
                </div>
                <div className="space-y-3">
                  <Label className="font-semibold">Child under 1 year:</Label>
                  <RadioGroup
                    value={formData.sleep.sleep_position_under_1}
                    onValueChange={(v) => updateSection('sleep', 'sleep_position_under_1', v)}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="back" id="pos-u1-back" />
                      <Label htmlFor="pos-u1-back">Back (required)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="side_stomach" id="pos-u1-side" />
                      <Label htmlFor="pos-u1-side">Side or stomach (physician statement attached)</Label>
                    </div>
                  </RadioGroup>
                </div>
                <Separator />
                <div className="space-y-3">
                  <Label className="font-semibold">Child 1 year and older:</Label>
                  <RadioGroup
                    value={formData.sleep.sleep_position_over_1}
                    onValueChange={(v) => updateSection('sleep', 'sleep_position_over_1', v)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="back" id="pos-o1-back" />
                      <Label htmlFor="pos-o1-back">Back</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="side_stomach" id="pos-o1-side" />
                      <Label htmlFor="pos-o1-side">Side or stomach</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CARE TAB (Diapering/Toileting) */}
          <TabsContent value="care" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bath className="w-4 h-4 text-blue-500" />
                  Diapering
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Diaper type:</Label>
                  <div className="flex gap-4">
                    {['cloth', 'disposable'].map(type => (
                      <label key={type} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.diapering.diaper_type?.includes(type)}
                          onCheckedChange={() => toggleArrayItem('diapering', 'diaper_type', type)}
                        />
                        <span className="capitalize">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Diapers provided by parent?</Label>
                  <RadioGroup
                    value={formData.diapering.diapers_provided_by_parent === true ? 'yes' : formData.diapering.diapers_provided_by_parent === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('diapering', 'diapers_provided_by_parent', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="diaper-parent-yes" />
                      <Label htmlFor="diaper-parent-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="diaper-parent-no" />
                      <Label htmlFor="diaper-parent-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Highly sensitive skin?</Label>
                    <RadioGroup
                      value={formData.diapering.sensitive_skin === true ? 'yes' : formData.diapering.sensitive_skin === false ? 'no' : ''}
                      onValueChange={(v) => updateSection('diapering', 'sensitive_skin', v === 'yes')}
                      className="flex gap-4"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="skin-yes" />
                        <Label htmlFor="skin-yes">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="skin-no" />
                        <Label htmlFor="skin-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div className="space-y-2">
                    <Label>Frequent diaper rash?</Label>
                    <RadioGroup
                      value={formData.diapering.frequent_diaper_rash === true ? 'yes' : formData.diapering.frequent_diaper_rash === false ? 'no' : ''}
                      onValueChange={(v) => updateSection('diapering', 'frequent_diaper_rash', v === 'yes')}
                      className="flex gap-4"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="rash-yes" />
                        <Label htmlFor="rash-yes">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="rash-no" />
                        <Label htmlFor="rash-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Lotions, powders, or salves used?</Label>
                  <RadioGroup
                    value={formData.diapering.lotions_used === true ? 'yes' : formData.diapering.lotions_used === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('diapering', 'lotions_used', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="lotion-yes" />
                      <Label htmlFor="lotion-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="lotion-no" />
                      <Label htmlFor="lotion-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.diapering.lotions_used && (
                    <Input value={formData.diapering.lotions_specify} onChange={(e) => updateSection('diapering', 'lotions_specify', e.target.value)} placeholder="Product name(s)" className="mt-2" />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Toilet Training</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Toilet training attempted?</Label>
                  <RadioGroup
                    value={formData.diapering.toilet_training === true ? 'yes' : formData.diapering.toilet_training === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('diapering', 'toilet_training', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="toilet-yes" />
                      <Label htmlFor="toilet-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="toilet-no" />
                      <Label htmlFor="toilet-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.diapering.toilet_training && (
                    <Textarea value={formData.diapering.toilet_training_routine} onChange={(e) => updateSection('diapering', 'toilet_training_routine', e.target.value)} placeholder="Describe routine" rows={2} className="mt-2" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Toilet seat type used at home:</Label>
                  <div className="flex flex-wrap gap-4">
                    {[['potty_chair', 'Potty chair'], ['special_seat', 'Special toilet seat'], ['regular', 'Regular toilet seat']].map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.diapering.toilet_seat_type?.includes(value)}
                          onCheckedChange={() => toggleArrayItem('diapering', 'toilet_seat_type', value)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  Verbal Communication
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Family's spoken language:</Label>
                  <div className="flex flex-wrap gap-4">
                    {['english', 'spanish', 'hmong', 'other'].map(lang => (
                      <label key={lang} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.verbal_communication.family_languages?.includes(lang)}
                          onCheckedChange={() => toggleArrayItem('verbal_communication', 'family_languages', lang)}
                        />
                        <span className="capitalize">{lang}</span>
                      </label>
                    ))}
                  </div>
                  {formData.verbal_communication.family_languages?.includes('other') && (
                    <Input value={formData.verbal_communication.language_other_specify} onChange={(e) => updateSection('verbal_communication', 'language_other_specify', e.target.value)} placeholder="Specify language" className="mt-2" />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Age child began talking</Label>
                    <Input value={formData.verbal_communication.age_began_talking} onChange={(e) => updateSection('verbal_communication', 'age_began_talking', e.target.value)} placeholder="e.g., 10 months" />
                  </div>
                  <div className="space-y-2">
                    <Label>Child speaks in:</Label>
                    <div className="flex gap-4 pt-2">
                      {['words', 'sentences'].map(type => (
                        <label key={type} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.verbal_communication.speaks_in?.includes(type)}
                            onCheckedChange={() => toggleArrayItem('verbal_communication', 'speaks_in', type)}
                          />
                          <span className="capitalize">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Words used to describe special needs</Label>
                  <Textarea value={formData.verbal_communication.special_needs_words} onChange={(e) => updateSection('verbal_communication', 'special_needs_words', e.target.value)} rows={2} placeholder="e.g., 'potty', 'baba' for bottle" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Heart className="w-4 h-4 text-pink-500" />
                  Comforting
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Does child have a fussy time?</Label>
                  <RadioGroup
                    value={formData.comforting.has_fussy_time === true ? 'yes' : formData.comforting.has_fussy_time === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('comforting', 'has_fussy_time', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="fussy-yes" />
                      <Label htmlFor="fussy-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="fussy-no" />
                      <Label htmlFor="fussy-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.comforting.has_fussy_time && (
                    <div className="space-y-2 mt-2">
                      <Input value={formData.comforting.fussy_time_specify} onChange={(e) => updateSection('comforting', 'fussy_time_specify', e.target.value)} placeholder="What time?" />
                      <Textarea value={formData.comforting.fussy_time_handling} onChange={(e) => updateSection('comforting', 'fussy_time_handling', e.target.value)} placeholder="How is it handled?" rows={2} />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Child likes to be:</Label>
                  <div className="flex flex-wrap gap-4">
                    {[['held', 'Held'], ['sung_to', 'Sung to'], ['rocked', 'Rocked'], ['read_to', 'Read to'], ['other', 'Other']].map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.comforting.child_likes?.includes(value)}
                          onCheckedChange={() => toggleArrayItem('comforting', 'child_likes', value)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                  {formData.comforting.child_likes?.includes('other') && (
                    <Input value={formData.comforting.child_likes_other} onChange={(e) => updateSection('comforting', 'child_likes_other', e.target.value)} placeholder="Specify" className="mt-2" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Special things you say or do to comfort child</Label>
                  <Textarea value={formData.comforting.comfort_methods} onChange={(e) => updateSection('comforting', 'comfort_methods', e.target.value)} rows={2} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DEVELOPMENT TAB */}
          <TabsContent value="development" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Smile className="w-4 h-4 text-yellow-500" />
                  Self-Expression
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>What causes your child to feel angry or frustrated?</Label>
                  <Textarea value={formData.self_expression.causes_anger} onChange={(e) => updateSection('self_expression', 'causes_anger', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>What frightens your child and how is it shown?</Label>
                  <Textarea value={formData.self_expression.what_frightens} onChange={(e) => updateSection('self_expression', 'what_frightens', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>How does your child express feelings of happiness, enjoyment, etc.?</Label>
                  <Textarea value={formData.self_expression.expresses_happiness} onChange={(e) => updateSection('self_expression', 'expresses_happiness', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Additional comments</Label>
                  <Textarea value={formData.self_expression.additional_comments} onChange={(e) => updateSection('self_expression', 'additional_comments', e.target.value)} rows={2} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-500" />
                  Physical and Social Development
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Is your child able to: (Check all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[['sit_alone', 'Sit up alone'], ['pull_up', 'Pull up'], ['crawl', 'Crawl'], ['walk_holding', 'Walk holding on'], ['walk_alone', 'Walk without support']].map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.physical_social.abilities?.includes(value)}
                          onCheckedChange={() => toggleArrayItem('physical_social', 'abilities', value)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Is your child used to playmates?</Label>
                  <RadioGroup
                    value={formData.physical_social.used_to_playmates === true ? 'yes' : formData.physical_social.used_to_playmates === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('physical_social', 'used_to_playmates', v === 'yes')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="playmates-yes" />
                      <Label htmlFor="playmates-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="playmates-no" />
                      <Label htmlFor="playmates-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label>Comments</Label>
                  <Textarea value={formData.physical_social.comments} onChange={(e) => updateSection('physical_social', 'comments', e.target.value)} rows={2} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  Miscellaneous
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Child's favorite indoor toys and activities</Label>
                  <Textarea value={formData.miscellaneous.favorite_indoor} onChange={(e) => updateSection('miscellaneous', 'favorite_indoor', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Child's favorite outdoor toys and activities</Label>
                  <Textarea value={formData.miscellaneous.favorite_outdoor} onChange={(e) => updateSection('miscellaneous', 'favorite_outdoor', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Additional info about child's habits, abilities, or personality</Label>
                  <Textarea value={formData.miscellaneous.additional_info} onChange={(e) => updateSection('miscellaneous', 'additional_info', e.target.value)} rows={3} placeholder="By providing complete information, you will assist staff in creating a positive experience for your child while in care." />
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary">
              <CardHeader className="pb-2 bg-primary/5 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Parent / Guardian Signature
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Signature (Type Full Name) *</Label>
                    <Input
                      value={formData.parent_signature}
                      onChange={(e) => setFormData({ ...formData, parent_signature: e.target.value })}
                      placeholder="Type your full name"
                      className="font-cursive text-lg"
                      data-testid="parent-signature"
                    />
                    <p className="text-xs text-muted-foreground">
                      By typing your name, you acknowledge this as your electronic signature.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Date Signed *</Label>
                    <Input
                      type="date"
                      value={formData.signature_date}
                      onChange={(e) => setFormData({ ...formData, signature_date: e.target.value })}
                      data-testid="signature-date"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-intake-final-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Intake Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DCFIntakeUnder2Form;
